import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { StudyNoteAnnotationInputs } from './StudyNoteAnnotationInputs';
import type { NoteAnnotationSelection } from './studyNotebookOperationsApi';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { StudyContextTools } from './StudyContextTools.tsx';
import { StudyExecutionStatus } from './StudyExecutionStatus.tsx';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import type { StudyNote } from './studyNotesApi';
import { StudyAudioScriptApproval } from './StudyAudioScriptApproval';
import {
  noteOperations,
  type NoteOperation,
  type NoteOperationKind,
  type NoteOperationSelection,
  type NoteRegion,
  type StudyFlashcard,
} from './studyNotebookOperationsApi';
const labels: Record<NoteOperationKind, string> = {
  knowledge: 'Rebuild learning points',
  simplify: 'Simplify',
  expand: 'Explain more',
  rewrite: 'Change wording',
  region: 'Ask about this area',
  flashcards: 'Flashcards',
  quiz: 'Quiz',
  audio: 'Audio recap',
};
const errorText = (error: unknown) =>
  error instanceof Error ? error.message : 'This action did not finish. Your notes are safe.';
export function StudyNotebookLearningActions({
  note,
  disabled,
  selection,
  region,
  onChanged,
  onUnsavedChange,
  knowledgeIds,
  subjectKey,
}: {
  note: StudyNote;
  disabled: boolean;
  selection?: { from: number; to: number } | null;
  region?: NoteRegion | null;
  onChanged: () => void;
  onUnsavedChange?: (dirty: boolean) => void;
  knowledgeIds?: string[];
  subjectKey?: string;
}) {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const requestedOperation = params.get('operation');
  const [returnIssue, setReturnIssue] = useState<string | null>(null);
  const consumedOperation = useRef('');
  const focusedOperation = useRef('');
  const resultHeading = useRef<HTMLHeadingElement>(null);
  const owner = useAuthStore(() => getKidSub());
  const [kind, setKind] = useState<NoteOperationKind | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [source, setSource] = useState<'saved_notes' | 'original_material'>('saved_notes');
  const [includeWorking, setIncludeWorking] = useState(false);
  const [annotations, setAnnotations] = useState<NoteAnnotationSelection[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ id: string; version: number; key: string } | null>(null);
  const [previewSelection, setPreviewSelection] = useState<NoteOperationSelection | null>(null);
  const createKey = useRef<{ scope: string; key: string } | null>(null);
  const applyKey = useRef<{ scope: string; key: string } | null>(null);
  const panel = useRef<HTMLElement>(null);
  const working = useQuery({
    queryKey: ['study-note-working-source', owner, note.id],
    queryFn: () => noteOperations.working(note.pages[0].document_id),
    enabled: Boolean(owner && includeWorking && expanded && note.pages[0]),
    retry: false,
    staleTime: 0,
  });
  const list = useQuery({
    queryKey: ['study-note-operations', owner, note.id],
    queryFn: () => noteOperations.list(note.id),
    enabled: Boolean(owner) && expanded,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (item) =>
          item.status === 'queued' || item.status === 'running' || item.status === 'refund_pending',
      )
        ? 2000
        : false,
  });
  const returnOperation = useQuery({
    queryKey: ['study-note-operation-return', owner, note.id, requestedOperation],
    queryFn: () => noteOperations.get(note.id, requestedOperation!),
    enabled: Boolean(owner && requestedOperation),
    retry: false,
    staleTime: 0,
  });
  useEffect(() => {
    if (region) {
      setKind('region');
      setInstruction('');
      panel.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [region]);
  useEffect(() => {
    if (region || selection) setExpanded(true);
  }, [region, selection]);
  const live = () => Boolean(owner) && getKidSub() === owner;
  const rangeNeeded = kind === 'simplify' || kind === 'expand' || kind === 'rewrite';
  const operationSelection: NoteOperationSelection = {
    expected_version: note.version,
    ...(['quiz', 'flashcards', 'audio'].includes(kind ?? '') && annotations.length
      ? { annotations }
      : {}),
    ...(!rangeNeeded && ['quiz', 'flashcards', 'audio'].includes(kind ?? '') ? { source } : {}),
    ...(!rangeNeeded &&
    ['quiz', 'flashcards', 'audio'].includes(kind ?? '') &&
    includeWorking &&
    working.data
      ? { working: { id: working.data.id, version: working.data.version } }
      : {}),
    ...(!rangeNeeded && kind !== 'knowledge' && knowledgeIds?.length
      ? { knowledge_ids: knowledgeIds }
      : {}),
    ...((rangeNeeded || (selectedOnly && source === 'saved_notes')) && selection ? selection : {}),
    ...(instruction.trim() ? { instruction: instruction.trim() } : {}),
    ...(kind === 'region' && region ? { region } : {}),
  };
  const canQuote = Boolean(
    kind &&
    !disabled &&
    (!rangeNeeded || selection) &&
    (!includeWorking || rangeNeeded || kind === 'region' || Boolean(working.data)) &&
    (kind !== 'region' || (region && instruction.trim())),
  );
  const quote = useQuery({
    queryKey: ['study-note-operation-quote', owner, note.id, kind, operationSelection],
    queryFn: () => noteOperations.quote(note.id, kind!, operationSelection),
    enabled: canQuote && live(),
    retry: false,
  });
  const create = useMutation({
    mutationFn: async () => {
      if (!live() || !kind || !quote.data || !canQuote)
        throw new Error('Save your notes and select a source first.');
      const scope = JSON.stringify({ id: note.id, kind, selection: operationSelection, owner });
      if (createKey.current?.scope !== scope)
        createKey.current = { scope, key: crypto.randomUUID() };
      setPreviewSelection(operationSelection);
      return noteOperations.create(
        note.id,
        kind,
        operationSelection,
        quote.data.stars,
        createKey.current.key,
      );
    },
    onSuccess: (operation) => {
      if (!live() || operation.notebook_id !== note.id) return;
      setActive(operation.id);
      createKey.current = null;
      void list.refetch();
    },
  });
  const apply = useMutation({
    mutationFn: async ({
      operation,
      reverse = false,
    }: {
      operation: NoteOperation;
      reverse?: boolean;
    }) => {
      if (!live() || disabled) throw new Error('Save your current edits first.');
      const scope = `${owner}:${operation.id}:${note.version}:${reverse}`;
      if (applyKey.current?.scope !== scope) applyKey.current = { scope, key: crypto.randomUUID() };
      return {
        saved: await noteOperations.change<{ version: number }>(
          note.id,
          operation.id,
          reverse ? 'undo' : 'apply',
          { expected_version: note.version, request_id: applyKey.current.key },
        ),
        operation,
        reverse,
      };
    },
    onSuccess: ({ saved, operation, reverse }) => {
      if (!live() || operation.notebook_id !== note.id) return;
      applyKey.current = null;
      setUndo(
        reverse ? null : { id: operation.id, version: saved.version, key: crypto.randomUUID() },
      );
      onChanged();
      void list.refetch();
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => {
      if (!live()) throw new Error('Sign in again.');
      return noteOperations.change(note.id, id, 'cancel');
    },
    onSuccess: () => void list.refetch(),
  });
  useEffect(() => {
    setActive(null);
    setUndo(null);
    setKind(null);
    setSource('saved_notes');
    setIncludeWorking(false);
    setSelectedOnly(false);
    setInstruction('');
    setPreviewSelection(null);
    setReturnIssue(null);
    consumedOperation.current = '';
    focusedOperation.current = '';
    createKey.current = null;
    applyKey.current = null;
  }, [owner, note.id]);
  const returned = returnOperation.data;
  const exactReturn =
    returned?.id === requestedOperation &&
    returned.notebook_id === note.id &&
    (!params.has('operationRevision') ||
      Number(params.get('operationRevision')) === returned.revision) &&
    (!params.has('notebookVersion') ||
      Number(params.get('notebookVersion')) === returned.source_version);
  const current =
    active === requestedOperation
      ? exactReturn
        ? returned
        : undefined
      : list.data?.items.find((operation) => operation.id === active);
  useEffect(() => {
    if (!requestedOperation || !exactReturn || current?.id !== requestedOperation) return;
    const scope = `${owner}:${note.id}:${location.key}:${current.id}:${current.revision}`;
    if (focusedOperation.current === scope) return;
    const frame = requestAnimationFrame(() => {
      const heading = resultHeading.current;
      if (!heading) return;
      focusedOperation.current = scope;
      heading.focus({ preventScroll: true });
      heading.scrollIntoView?.({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [
    requestedOperation,
    exactReturn,
    current?.id,
    current?.revision,
    owner,
    note.id,
    location.key,
  ]);
  useEffect(() => {
    if (requestedOperation) setExpanded(true);
  }, [requestedOperation, owner, note.id]);
  useEffect(() => {
    if (!requestedOperation || returnOperation.isPending) return;
    if (returnOperation.isError) {
      setReturnIssue(
        'This saved explanation could not be opened. Its source may be unavailable; retry before opening another version.',
      );
      setActive(null);
      return;
    }
    const scope = `${owner}:${note.id}:${location.key}:${requestedOperation}:${returnOperation.data?.revision}`;
    if (consumedOperation.current === scope) return;
    consumedOperation.current = scope;
    const saved = returnOperation.data;
    const revision = params.get('operationRevision');
    const version = params.get('notebookVersion');
    if (
      !saved ||
      saved.id !== requestedOperation ||
      saved.notebook_id !== note.id ||
      saved.status === 'expired'
    ) {
      setReturnIssue(
        'This saved explanation is no longer available. Its source may have been removed or expired.',
      );
      setActive(null);
    } else if (
      (revision && Number(revision) !== saved.revision) ||
      (version && Number(version) !== saved.source_version)
    ) {
      setReturnIssue(
        'This saved item changed after you opened the tool. Open it from the saved items list to see its current state.',
      );
      setActive(null);
    } else {
      setReturnIssue(null);
      setActive(saved.id);
      setKind(saved.kind);
    }
  }, [
    requestedOperation,
    returnOperation.data,
    returnOperation.isPending,
    returnOperation.isError,
    owner,
    note.id,
    location.key,
    params,
  ]);
  const editing = current && ['simplify', 'expand', 'rewrite'].includes(current.kind);
  const beforeText =
    previewSelection?.from !== undefined
      ? note.body.slice(previewSelection.from, previewSelection.to)
      : '';
  return (
    <section
      ref={panel}
      aria-label="Learn from these notes"
      className="mt-5 border-t border-hairline pt-4"
    >
      <details open={expanded} onToggle={(event) => setExpanded(event.currentTarget.open)}>
        <summary className="min-h-11 cursor-pointer font-bold">
          {knowledgeIds?.length ? 'Practise selected ideas' : 'Use my saved notes'}
        </summary>
        {returnIssue && (
          <p role="status" className="my-3 rounded-xl bg-wash-sunshine p-3 text-sm">
            {returnIssue}
          </p>
        )}
        {returnOperation.isError && (
          <button
            type="button"
            className="btn-pill-secondary"
            onClick={() => void returnOperation.refetch()}
          >
            Retry this saved explanation
          </button>
        )}
        <p className="my-3 text-sm text-slate2">
          Saved version {note.version} · your edits are included. Save before using AI.
        </p>
        {disabled && (
          <p role="status" className="text-sm">
            Finish saving your edits first.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {(['flashcards', 'quiz', 'audio'] as const).map((item) => (
            <button
              type="button"
              className="btn-pill-secondary"
              disabled={disabled || create.isPending}
              key={item}
              onClick={() => {
                setKind(item);
                setInstruction('');
                setActive(null);
              }}
            >
              {labels[item]}
            </button>
          ))}
        </div>
        {selection && (
          <div
            role="toolbar"
            aria-label="Help with selected words"
            className="mt-3 flex flex-wrap gap-2 rounded-xl border border-hairline bg-surface p-2"
          >
            {(['simplify', 'expand', 'rewrite'] as const).map((item) => (
              <button
                type="button"
                className="btn-pill-secondary"
                key={item}
                disabled={disabled || create.isPending}
                onClick={() => {
                  setKind(item);
                  setActive(null);
                }}
              >
                {labels[item]}
              </button>
            ))}
          </div>
        )}
        {kind && (
          <div className="my-4 rounded-2xl border border-hairline p-4">
            <h3 className="font-bold">{labels[kind]}</h3>
            <p className="mt-2 text-sm">
              {kind === 'region'
                ? `Source slide/page ${region?.page_number ?? ''} · selected image area`
                : rangeNeeded
                  ? 'Only your selected words will change after you review and Apply.'
                  : 'Use this saved note version; no need to select the file again.'}
            </p>
            {['quiz', 'flashcards', 'audio'].includes(kind) && (
              <label className="my-3 block text-sm font-bold">
                Use content from
                <select
                  className="ml-2 rounded-xl border border-hairline bg-canvas-pure px-3 py-2"
                  value={source}
                  disabled={create.isPending || disabled}
                  onChange={(event) => {
                    setSource(event.target.value as typeof source);
                    setSelectedOnly(false);
                    setActive(null);
                  }}
                >
                  <option value="saved_notes">My saved notes, including my edits</option>
                  <option value="original_material">
                    Original material · analysed source pages
                  </option>
                </select>
              </label>
            )}
            {['quiz', 'flashcards', 'audio'].includes(kind) && (
              <StudyNoteAnnotationInputs
                documents={[...new Set(note.pages.map((page) => page.document_id))]}
                selected={annotations}
                onChange={setAnnotations}
                disabled={disabled || create.isPending}
              />
            )}
            {['quiz', 'flashcards', 'audio'].includes(kind) && (
              <div className="my-3 text-sm">
                <label className="flex min-h-11 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeWorking}
                    disabled={disabled || create.isPending}
                    onChange={(event) => setIncludeWorking(event.target.checked)}
                  />
                  Include my saved writing and working
                </label>
                {includeWorking && (
                  <>
                    {working.isPending ? (
                      <p role="status">Finding saved working…</p>
                    ) : working.data ? (
                      <p>
                        Saved working v{working.data.version} · {working.data.stroke_count} pen
                        strokes. Only this saved version is included; unreadable parts will be
                        listed.
                      </p>
                    ) : (
                      <p role="status">
                        No saved working is available. Write and save on this material first, then{' '}
                        <button
                          className="underline"
                          type="button"
                          onClick={() => void working.refetch()}
                        >
                          check again
                        </button>
                        , or untick this option.
                      </p>
                    )}
                    {working.error && (
                      <p role="alert">Your saved working could not load. Retry or leave it out.</p>
                    )}
                  </>
                )}
              </div>
            )}
            {!rangeNeeded && kind !== 'region' && source === 'saved_notes' && selection && (
              <label className="my-3 flex min-h-11 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedOnly}
                  onChange={(e) => setSelectedOnly(e.target.checked)}
                />
                Use only my selected section
              </label>
            )}
            {(kind === 'rewrite' || kind === 'region') && (
              <label className="mt-3 block text-sm font-bold">
                {kind === 'region' ? 'What is confusing?' : 'How should it change?'}
                <textarea
                  className="input-k12 mt-2 min-h-20 w-full"
                  value={instruction}
                  maxLength={500}
                  onChange={(e) => setInstruction(e.target.value)}
                />
              </label>
            )}
            {quote.isError && (
              <p role="alert" className="mt-3 text-sm">
                {errorText(quote.error)}
              </p>
            )}
            <button
              type="button"
              className="btn-pill-primary mt-3"
              disabled={!canQuote || !quote.data || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending
                ? 'Starting…'
                : quote.data
                  ? kind === 'audio'
                    ? `Prepare script + audio · ${quote.data.stars}★`
                    : `Create ${labels[kind].toLowerCase()} · ${quote.data.stars}★`
                  : 'Checking availability…'}
            </button>
            {create.isError && (
              <p role="alert" className="mt-3 text-sm">
                {errorText(create.error)} Retry keeps the same request.
              </p>
            )}
          </div>
        )}
        {list.isError && (
          <button
            type="button"
            className="btn-pill-secondary mt-3"
            onClick={() => void list.refetch()}
          >
            Retry saved learning items
          </button>
        )}
        {list.data && (
          <div className="my-4 flex flex-wrap gap-2">
            {list.data.items.map((operation) => (
              <button
                type="button"
                className="btn-pill-secondary"
                key={operation.id}
                onClick={() => {
                  setParams(
                    (current) => {
                      const next = new URLSearchParams(current);
                      next.delete('operation');
                      next.delete('operationRevision');
                      next.delete('notebookVersion');
                      return next;
                    },
                    { replace: true },
                  );
                  setActive(operation.id);
                  setKind(operation.kind);
                  setReturnIssue(null);
                }}
              >
                {labels[operation.kind]} · v{operation.source_version}
                {operation.stale ? ' · older notes' : ''}
              </button>
            ))}
          </div>
        )}
        {current && (
          <article className="rounded-2xl border border-hairline p-4">
            <h3 ref={resultHeading} tabIndex={-1} className="scroll-mt-24 font-bold">
              {labels[current.kind]} · saved note version {current.source_version}
            </h3>
            <p className="mt-2 text-sm text-slate2">
              {current.source === 'original_material'
                ? 'From analysed original source passages; unread pages are not included.'
                : 'From the saved notes, including your edits.'}
            </p>
            {current.working && (
              <p className="mt-2 text-sm">
                Includes saved writing/working v{current.working.version}. The original drawing is
                kept.
              </p>
            )}
            {Boolean(current.annotations?.length) && (
              <p className="mt-2 text-sm">
                Includes {current.annotations!.length} explicitly selected saved page marks.
                Unreadable parts are listed below.
              </p>
            )}
            {current.result?.working_coverage && (
              <div role="status" className="my-3 text-sm">
                <p>
                  {current.result.working_coverage.status === 'included'
                    ? 'AI read the selected working. Check its reading against your drawing.'
                    : 'Some or all working could not be read and was left out.'}
                </p>
                {current.result.working_coverage.omissions.map((text, index) => (
                  <p key={index}>{text}</p>
                ))}
              </div>
            )}
            {current.status === 'awaiting_approval' && (
              <StudyAudioScriptApproval
                key={`${owner}:${current.id}`}
                noteId={note.id}
                operation={current}
                onUnsavedChange={onUnsavedChange}
                disabled={disabled || current.stale}
                onChanged={() => void list.refetch()}
              />
            )}
            {current.stale && (
              <p className="my-3 rounded-xl bg-wash-sunshine p-3 text-sm">
                Your notes changed. This item keeps its earlier source snapshot. Create from the
                current version to update it.
              </p>
            )}
            {['queued', 'running', 'awaiting_approval', 'refund_pending'].includes(
              current.status,
            ) && (
              <>
                {current.status === 'queued' || current.status === 'running' ? (
                  <StudyExecutionStatus
                    phase={
                      current.status === 'queued'
                        ? 'queued'
                        : current.kind === 'knowledge'
                          ? 'knowledge'
                          : current.kind === 'quiz' || current.kind === 'flashcards'
                            ? 'practice'
                            : 'notes'
                    }
                    label={
                      current.status === 'queued'
                        ? undefined
                        : current.kind === 'flashcards'
                          ? 'Making your study cards'
                          : current.kind === 'audio'
                            ? 'Preparing your audio recap'
                            : current.kind === 'region'
                              ? 'Looking at this area'
                              : ['simplify', 'expand', 'rewrite'].includes(current.kind)
                                ? 'Working on this passage'
                                : undefined
                    }
                    detail="Your saved notes stay available. You can return later."
                  />
                ) : (
                  <p role="status" className="my-3">
                    {current.status === 'refund_pending'
                      ? 'A refund is still being settled.'
                      : 'Your script is ready to review. Audio waits for your confirmation.'}
                  </p>
                )}
                {current.status !== 'refund_pending' && (
                  <button
                    type="button"
                    className="btn-pill-secondary"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(current.id)}
                  >
                    Cancel and refund
                  </button>
                )}
              </>
            )}
            {current.status === 'failed' && (
              <p role="alert" className="my-3">
                This did not finish. {current.stars_refunded} of {current.stars_charged} Stars
                returned. Your notes are unchanged.
              </p>
            )}
            {current.status === 'ready' && current.result?.text && (
              <>
                <p className="my-4 whitespace-pre-wrap text-[17px] leading-relaxed">
                  {current.result.text}
                </p>
                <StudyContextTools
                  compact
                  disabled={disabled || apply.isPending || cancel.isPending}
                  context={{
                    subjectKey: subjectKey ?? note.subject_key ?? '',
                    label: labels[current.kind],
                    text: current.result.text,
                    sourceVersion: current.source_version,
                    returnTo: `${location.pathname}?${new URLSearchParams({ ...Object.fromEntries([...params].filter(([key]) => !['knowledgePoint', 'knowledgeVersion'].includes(key))), operation: current.id, operationRevision: String(current.revision), notebookVersion: String(current.source_version) })}`,
                  }}
                />
                {editing && (
                  <>
                    <details>
                      <summary className="min-h-11 cursor-pointer text-sm font-bold">
                        Compare with my selected words
                      </summary>
                      <p className="whitespace-pre-wrap text-sm">
                        {beforeText ||
                          'This saved suggestion belongs to its original version and range.'}
                      </p>
                    </details>
                    <button
                      type="button"
                      className="btn-pill-primary"
                      disabled={disabled || current.stale || apply.isPending}
                      onClick={() => apply.mutate({ operation: current })}
                    >
                      Apply to my notes
                    </button>
                  </>
                )}
              </>
            )}
            {current.result?.cards && (
              <StudyFlashcardDeck
                key={`${current.id}:${current.revision}`}
                noteId={note.id}
                operation={current}
                onChanged={() => void list.refetch()}
              />
            )}
            {current.practice_set_id && (
              <Link
                className="btn-pill-primary mt-4 inline-flex"
                to={`/learn/study/materials/${note.pages[0].document_id}/analysis?analysis=${current.result?.analysis_id}&details=1&practiceSet=${current.practice_set_id}`}
              >
                Start quiz · answers after my attempt
              </Link>
            )}
            {current.audio && (
              <>
                <audio
                  controls
                  preload="none"
                  src={current.audio.url}
                  className="mt-4 w-full"
                  onError={() => void list.refetch()}
                />
                <details className="mt-3">
                  <summary className="min-h-11 cursor-pointer font-bold">
                    Read the audio script
                  </summary>
                  <p className="whitespace-pre-wrap text-sm">{current.result?.script}</p>
                </details>
              </>
            )}
          </article>
        )}
        {undo && note.version === undo.version && current?.id === undo.id && (
          <button
            type="button"
            className="btn-pill-secondary mt-3"
            disabled={disabled || apply.isPending}
            onClick={() => apply.mutate({ operation: current, reverse: true })}
          >
            Undo AI change · keep version history
          </button>
        )}
        {apply.isError && (
          <p role="alert" className="mt-3 text-sm">
            {errorText(apply.error)}
          </p>
        )}
      </details>
    </section>
  );
}
function StudyFlashcardDeck({
  noteId,
  operation,
  onChanged,
}: {
  noteId: string;
  operation: NoteOperation;
  onChanged: () => void;
}) {
  const owner = useAuthStore(() => getKidSub());
  const [index, setIndex] = useState(0),
    [flipped, setFlipped] = useState(false),
    [editing, setEditing] = useState(false);
  const [cards, setCards] = useState<StudyFlashcard[]>(operation.result?.cards ?? []);
  const key = useRef<{ scope: string; key: string } | null>(null);
  const current = cards[index];
  const change = useMutation({
    mutationFn: async (rating?: 'again' | 'good') => {
      if (!owner || getKidSub() !== owner) throw new Error('Sign in again.');
      const payload = {
        expected_revision: operation.revision,
        ...(rating ? { review: { index, rating } } : { cards }),
      };
      const scope = JSON.stringify(payload);
      if (key.current?.scope !== scope) key.current = { scope, key: crypto.randomUUID() };
      return noteOperations.change(noteId, operation.id, 'cards', {
        ...payload,
        request_id: key.current.key,
      });
    },
    onSuccess: () => {
      key.current = null;
      onChanged();
    },
  });
  if (!current) return null;
  return (
    <section aria-label="My flashcards" className="mt-4">
      <p className="text-sm text-slate2">
        Card {index + 1} of {cards.length} ·{' '}
        {operation.result?.reviews?.[index]?.due_at
          ? `Review due ${new Date(operation.result.reviews[index].due_at).toLocaleDateString()}`
          : 'Not reviewed yet'}
      </p>
      {editing ? (
        <>
          <label className="mt-3 block text-sm font-bold">
            Question
            <textarea
              className="input-k12 mt-2 w-full"
              maxLength={400}
              value={current.front}
              onChange={(e) =>
                setCards(
                  cards.map((card, i) => (i === index ? { ...card, front: e.target.value } : card)),
                )
              }
            />
          </label>
          <label className="mt-3 block text-sm font-bold">
            Explanation
            <textarea
              className="input-k12 mt-2 w-full"
              maxLength={1200}
              value={current.back}
              onChange={(e) =>
                setCards(
                  cards.map((card, i) => (i === index ? { ...card, back: e.target.value } : card)),
                )
              }
            />
          </label>
          <button
            type="button"
            className="btn-pill-primary mt-3"
            disabled={change.isPending}
            onClick={() => change.mutate(undefined)}
          >
            Save my card edits · free
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="my-4 min-h-40 w-full rounded-2xl border border-hairline bg-canvas p-5 text-left text-[17px] leading-relaxed"
            onClick={() => setFlipped(!flipped)}
            aria-label={flipped ? 'Show card question' : 'Reveal explanation'}
          >
            {flipped ? current.back : current.front}
          </button>
          {flipped && (
            <>
              <p className="mb-3 text-sm text-slate2">
                From my note snapshot: “{current.evidence}”
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-pill-secondary"
                  disabled={change.isPending}
                  onClick={() => change.mutate('again')}
                >
                  Try again
                </button>
                <button
                  type="button"
                  className="btn-pill-primary"
                  disabled={change.isPending}
                  onClick={() => change.mutate('good')}
                >
                  I understood this
                </button>
              </div>
            </>
          )}
        </>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="btn-pill-secondary"
          type="button"
          disabled={index === 0 || change.isPending || editing}
          onClick={() => {
            setIndex(index - 1);
            setFlipped(false);
          }}
        >
          Previous
        </button>
        <button
          className="btn-pill-secondary"
          type="button"
          disabled={index === cards.length - 1 || change.isPending || editing}
          onClick={() => {
            setIndex(index + 1);
            setFlipped(false);
          }}
        >
          Next
        </button>
        <button
          className="btn-pill-secondary"
          type="button"
          disabled={change.isPending}
          onClick={() => {
            if (editing) setCards(operation.result?.cards ?? []);
            setEditing(!editing);
          }}
        >
          {editing ? 'Cancel editing' : 'Edit my card'}
        </button>
      </div>
      {change.isError && (
        <p role="alert" className="mt-3 text-sm">
          {errorText(change.error)}
        </p>
      )}
    </section>
  );
}
